/**
 * Company financial-period calendar for reporting.
 *
 * Rail operators typically run 13 four-week periods. Period 1 starts on a
 * company-chosen commencement date (must be a Sunday or Monday). A leftover
 * 53rd week is attached to period 13.
 */

export type WeekStartsOn = 0 | 1;

export type ReportingCalendarConfig = {
  /** 1 = January, 4 = April (any 1–12 is accepted). */
  fyStartMonth: number;
  /** 0 = Sunday, 1 = Monday. */
  weekStartsOn: WeekStartsOn;
  /** Period 1 commencement as MM-DD (e.g. 01-04 = 4 January). */
  p1CommencementMd: string;
};

export const DEFAULT_REPORTING_CALENDAR: ReportingCalendarConfig = {
  fyStartMonth: 1,
  weekStartsOn: 1,
  p1CommencementMd: "01-01",
};

export type ReportingPeriodWeek = {
  fyStartYear: number;
  period: number;
  weekInPeriod: number;
  weekInYear: number;
  start: string;
  end: string;
};

export type ReportingPeriod = {
  fyStartYear: number;
  period: number;
  weeks: number;
  start: string;
  end: string;
};

export type ReportingDateLocation = {
  fyStartYear: number;
  period: number;
  weekInPeriod: number;
  weekInYear: number;
  weeksInYear: number;
};

const MS_PER_DAY = 86_400_000;

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

export function normalizeFyStartMonth(raw: unknown): number {
  const n = typeof raw === "number" ? raw : Number.parseInt(String(raw ?? ""), 10);
  if (Number.isInteger(n) && n >= 1 && n <= 12) return n;
  return DEFAULT_REPORTING_CALENDAR.fyStartMonth;
}

export function normalizeWeekStartsOn(raw: unknown): WeekStartsOn {
  if (raw === "sunday" || raw === "Sunday" || raw === 0 || raw === "0") return 0;
  if (raw === "monday" || raw === "Monday" || raw === 1 || raw === "1") return 1;
  const n = Number.parseInt(String(raw ?? ""), 10);
  if (n === 0) return 0;
  return 1;
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function normalizeP1CommencementMd(raw: unknown, fyStartMonth = DEFAULT_REPORTING_CALENDAR.fyStartMonth): string {
  const text = String(raw ?? "").trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  const md = iso ? `${iso[2]}-${iso[3]}` : /^(\d{2})-(\d{2})$/.exec(text) ? text : "";
  const match = /^(\d{2})-(\d{2})$/.exec(md);
  if (match) {
    const month = Number.parseInt(match[1], 10);
    const day = Number.parseInt(match[2], 10);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) return `${pad2(month)}-${pad2(day)}`;
  }
  return `${pad2(normalizeFyStartMonth(fyStartMonth))}-01`;
}

export function normalizeReportingCalendar(raw: unknown): ReportingCalendarConfig {
  const obj = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const fyStartMonth = normalizeFyStartMonth(obj.fyStartMonth ?? obj.reporting_fy_start_month ?? obj.reportingFyStartMonth);
  return {
    fyStartMonth,
    weekStartsOn: normalizeWeekStartsOn(obj.weekStartsOn ?? obj.reporting_week_starts_on ?? obj.reportingWeekStartsOn),
    p1CommencementMd: normalizeP1CommencementMd(
      obj.p1CommencementMd ?? obj.reporting_p1_commencement_md ?? obj.reportingP1CommencementMd,
      fyStartMonth,
    ),
  };
}

export function ymdFromParts(year: number, month: number, day: number): string {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

export function ymdFromUtcDate(dt: Date): string {
  return ymdFromParts(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
}

export function utcDateFromYmd(ymd: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd);
  if (!m) return new Date(NaN);
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

export function addDaysYmd(ymd: string, days: number): string {
  const dt = utcDateFromYmd(ymd);
  dt.setUTCDate(dt.getUTCDate() + days);
  return ymdFromUtcDate(dt);
}

export function diffDaysYmd(fromYmd: string, toYmd: string): number {
  return Math.round((utcDateFromYmd(toYmd).getTime() - utcDateFromYmd(fromYmd).getTime()) / MS_PER_DAY);
}

function weekdayUtc(ymd: string): number {
  return utcDateFromYmd(ymd).getUTCDay();
}

/** First week-start day on or before `ymd` (the week containing that date). */
export function startOfWeekOnOrBefore(ymd: string, weekStartsOn: WeekStartsOn): string {
  const dow = weekdayUtc(ymd);
  const offset = (dow - weekStartsOn + 7) % 7;
  return addDaysYmd(ymd, -offset);
}

/** First week-start day on or after `ymd`. */
export function startOfWeekOnOrAfter(ymd: string, weekStartsOn: WeekStartsOn): string {
  const dow = weekdayUtc(ymd);
  const offset = (weekStartsOn - dow + 7) % 7;
  return addDaysYmd(ymd, offset);
}

export function financialYearStartYmd(startYear: number, config: ReportingCalendarConfig): string {
  const weekStartsOn = normalizeWeekStartsOn(config.weekStartsOn);
  const md = normalizeP1CommencementMd(config.p1CommencementMd, config.fyStartMonth);
  const month = Number.parseInt(md.slice(0, 2), 10);
  const day = Number.parseInt(md.slice(3, 5), 10);
  const clampedDay = Math.min(Math.max(day, 1), daysInMonth(startYear, month));
  const nominal = ymdFromParts(startYear, month, clampedDay);
  return startOfWeekOnOrAfter(nominal, weekStartsOn);
}

/** Suggested P1 start: first week-start on or after the 1st of the FY month. */
export function suggestedP1CommencementYmd(startYear: number, fyStartMonth: number, weekStartsOn: WeekStartsOn): string {
  const month = normalizeFyStartMonth(fyStartMonth);
  return startOfWeekOnOrAfter(ymdFromParts(startYear, month, 1), normalizeWeekStartsOn(weekStartsOn));
}

export function calendarConfigFromCommencementYmd(
  ymd: string,
  fallback: Pick<ReportingCalendarConfig, "fyStartMonth" | "weekStartsOn">,
): ReportingCalendarConfig {
  const day = ymd.slice(0, 10);
  const dt = utcDateFromYmd(day);
  if (Number.isNaN(dt.getTime())) {
    return normalizeReportingCalendar(fallback);
  }
  const dow = dt.getUTCDay();
  const weekStartsOn: WeekStartsOn = dow === 0 || dow === 1 ? dow : normalizeWeekStartsOn(fallback.weekStartsOn);
  const snapped = startOfWeekOnOrAfter(day, weekStartsOn);
  const snappedDt = utcDateFromYmd(snapped);
  const month = snappedDt.getUTCMonth() + 1;
  const fyStartMonth = month === 4 || month === 3 ? 4 : month === 1 || month === 12 ? 1 : fallback.fyStartMonth;
  return {
    fyStartMonth,
    weekStartsOn,
    p1CommencementMd: `${pad2(month)}-${pad2(snappedDt.getUTCDate())}`,
  };
}

export function weeksInFinancialYear(startYear: number, config: ReportingCalendarConfig): number {
  const start = financialYearStartYmd(startYear, config);
  const next = financialYearStartYmd(startYear + 1, config);
  return Math.round(diffDaysYmd(start, next) / 7);
}

export function periodCountForYear(startYear: number, config: ReportingCalendarConfig): number {
  return Math.min(13, Math.ceil(weeksInFinancialYear(startYear, config) / 4));
}

export function weeksInPeriod(startYear: number, period: number, config: ReportingCalendarConfig): number {
  const totalWeeks = weeksInFinancialYear(startYear, config);
  if (period < 1 || period > 13) return 0;
  if (period < 13) return 4;
  return Math.max(4, totalWeeks - 48);
}

export function financialYearForDate(dateYmd: string, config: ReportingCalendarConfig): number {
  const dt = utcDateFromYmd(dateYmd);
  if (Number.isNaN(dt.getTime())) return new Date().getUTCFullYear();
  const calendarYear = dt.getUTCFullYear();
  const thisStart = financialYearStartYmd(calendarYear, config);
  return dateYmd >= thisStart ? calendarYear : calendarYear - 1;
}

export function locateDateInReportingCalendar(dateYmd: string, config: ReportingCalendarConfig): ReportingDateLocation | null {
  const day = dateYmd.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const fyStartYear = financialYearForDate(day, config);
  const start = financialYearStartYmd(fyStartYear, config);
  const next = financialYearStartYmd(fyStartYear + 1, config);
  if (day < start || day >= next) return null;
  const weekInYear = Math.floor(diffDaysYmd(start, day) / 7) + 1;
  const totalWeeks = weeksInFinancialYear(fyStartYear, config);
  const period = Math.min(13, Math.ceil(weekInYear / 4));
  const weekInPeriod = period < 13 ? ((weekInYear - 1) % 4) + 1 : weekInYear - 48;
  return { fyStartYear, period, weekInPeriod, weekInYear, weeksInYear: totalWeeks };
}

export function listPeriodWeeks(startYear: number, config: ReportingCalendarConfig): ReportingPeriodWeek[] {
  const start = financialYearStartYmd(startYear, config);
  const totalWeeks = weeksInFinancialYear(startYear, config);
  const rows: ReportingPeriodWeek[] = [];
  for (let weekInYear = 1; weekInYear <= totalWeeks; weekInYear += 1) {
    const period = Math.min(13, Math.ceil(weekInYear / 4));
    const weekInPeriod = period < 13 ? ((weekInYear - 1) % 4) + 1 : weekInYear - 48;
    const weekStart = addDaysYmd(start, (weekInYear - 1) * 7);
    rows.push({
      fyStartYear: startYear,
      period,
      weekInPeriod,
      weekInYear,
      start: weekStart,
      end: addDaysYmd(weekStart, 6),
    });
  }
  return rows;
}

export function listPeriods(startYear: number, config: ReportingCalendarConfig): ReportingPeriod[] {
  const weeks = listPeriodWeeks(startYear, config);
  const byPeriod = new Map<number, ReportingPeriodWeek[]>();
  for (const week of weeks) {
    const list = byPeriod.get(week.period) ?? [];
    list.push(week);
    byPeriod.set(week.period, list);
  }
  return Array.from(byPeriod.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([period, list]) => ({
      fyStartYear: startYear,
      period,
      weeks: list.length,
      start: list[0].start,
      end: list[list.length - 1].end,
    }));
}

export function formatFinancialYearLabel(startYear: number, fyStartMonth: number): string {
  const month = normalizeFyStartMonth(fyStartMonth);
  if (month === 1) return String(startYear);
  const endYear = startYear + 1;
  return `${startYear}/${String(endYear).slice(-2)}`;
}

export function periodKey(fyStartYear: number, period: number): string {
  return `fy${fyStartYear}-p${pad2(period)}`;
}

export function weekKey(fyStartYear: number, period: number, weekInPeriod: number): string {
  return `${periodKey(fyStartYear, period)}-w${pad2(weekInPeriod)}`;
}

export function mergedPeriodKey(period: number): string {
  return `p-${pad2(period)}`;
}

export function mergedWeekKey(period: number, weekInPeriod: number): string {
  return `${mergedPeriodKey(period)}-w${pad2(weekInPeriod)}`;
}

export type ReportingTrendGrain = "period" | "week";

export type ReportingDateFilter = {
  years: number[];
  periods: number[];
  weeks: number[];
};

export function parseIdList(raw: unknown, min: number, max: number): number[] {
  const text = typeof raw === "string" ? raw : "";
  const ids = text
    .split(",")
    .map((part) => Number.parseInt(part.trim(), 10))
    .filter((id) => Number.isInteger(id) && id >= min && id <= max);
  return Array.from(new Set(ids)).sort((a, b) => a - b);
}

export function dateMatchesReportingFilter(
  dateYmd: string,
  config: ReportingCalendarConfig,
  filter: ReportingDateFilter,
): boolean {
  const loc = locateDateInReportingCalendar(dateYmd, config);
  if (!loc) return false;
  if (filter.years.length > 0 && !filter.years.includes(loc.fyStartYear)) return false;
  if (filter.periods.length > 0 && !filter.periods.includes(loc.period)) return false;
  if (filter.weeks.length > 0 && !filter.weeks.includes(loc.weekInPeriod)) return false;
  return true;
}

export function resolveTrendGrain(filter: Pick<ReportingDateFilter, "periods" | "weeks">): ReportingTrendGrain {
  return filter.periods.length > 0 || filter.weeks.length > 0 ? "week" : "period";
}

export function trendBucketForDate(
  dateYmd: string,
  config: ReportingCalendarConfig,
  grain: ReportingTrendGrain,
): string | null {
  const loc = locateDateInReportingCalendar(dateYmd, config);
  if (!loc) return null;
  return grain === "week" ? weekKey(loc.fyStartYear, loc.period, loc.weekInPeriod) : periodKey(loc.fyStartYear, loc.period);
}

export function listTrendBuckets(
  config: ReportingCalendarConfig,
  filter: ReportingDateFilter,
): { key: string; label: string; start: string; end: string }[] {
  const years = filter.years.length > 0 ? filter.years : [financialYearForDate(ymdFromUtcDate(new Date()), config)];
  const grain = resolveTrendGrain(filter);
  const buckets: { key: string; label: string; start: string; end: string }[] = [];
  for (const year of years) {
    const weeks = listPeriodWeeks(year, config);
    if (grain === "period") {
      const periods = listPeriods(year, config);
      for (const period of periods) {
        buckets.push({
          key: periodKey(year, period.period),
          label: `P${period.period}`,
          start: period.start,
          end: period.end,
        });
      }
      continue;
    }
    for (const week of weeks) {
      if (filter.periods.length > 0 && !filter.periods.includes(week.period)) continue;
      if (filter.weeks.length > 0 && !filter.weeks.includes(week.weekInPeriod)) continue;
      buckets.push({
        key: weekKey(year, week.period, week.weekInPeriod),
        label: filter.periods.length === 1 ? `W${week.weekInPeriod}` : `P${week.period} W${week.weekInPeriod}`,
        start: week.start,
        end: week.end,
      });
    }
  }
  return buckets;
}

export function formatTrendBucketLabel(key: string): string {
  const fullWeek = /^fy(\d{4})-p(\d{2})-w(\d{2})$/.exec(key);
  if (fullWeek) return `P${Number(fullWeek[2])} W${Number(fullWeek[3])}`;
  const fullPeriod = /^fy(\d{4})-p(\d{2})$/.exec(key);
  if (fullPeriod) return `P${Number(fullPeriod[2])}`;
  const mergedWeek = /^p-(\d{2})-w(\d{2})$/.exec(key);
  if (mergedWeek) return `P${Number(mergedWeek[1])} W${Number(mergedWeek[2])}`;
  const mergedPeriod = /^p-(\d{2})$/.exec(key);
  if (mergedPeriod) return `P${Number(mergedPeriod[1])}`;
  const month = /^(\d{4})-(\d{2})$/.exec(key);
  const mergedMonth = /^m-(\d{2})$/.exec(key);
  if (month || mergedMonth) {
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const monthIndex = Number.parseInt((month?.[2] ?? mergedMonth?.[1]) as string, 10) - 1;
    return monthNames[monthIndex] ?? key;
  }
  return key;
}

export function trendBucketMergeKey(key: string): string | null {
  const fullWeek = /^fy\d{4}-p(\d{2})-w(\d{2})$/.exec(key);
  if (fullWeek) return mergedWeekKey(Number(fullWeek[1]), Number(fullWeek[2]));
  const fullPeriod = /^fy\d{4}-p(\d{2})$/.exec(key);
  if (fullPeriod) return mergedPeriodKey(Number(fullPeriod[1]));
  const month = /^(?:\d{4}-|m-)(\d{2})$/.exec(key);
  if (month) return `m-${month[1]}`;
  if (/^p-\d{2}(?:-w\d{2})?$/.test(key) || /^m-\d{2}$/.test(key)) return key;
  return null;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function formatYmdUk(ymd: string): string {
  const dt = utcDateFromYmd(ymd);
  if (Number.isNaN(dt.getTime())) return ymd;
  const weekday = WEEKDAY_NAMES[dt.getUTCDay()];
  const day = dt.getUTCDate();
  const month = MONTH_NAMES[dt.getUTCMonth()];
  return `${weekday} ${day} ${month} ${dt.getUTCFullYear()}`;
}

export function formatYmdShort(ymd: string): string {
  const dt = utcDateFromYmd(ymd);
  if (Number.isNaN(dt.getTime())) return ymd;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${dt.getUTCDate()} ${months[dt.getUTCMonth()]} ${dt.getUTCFullYear()}`;
}

export function formatYmdRangeShort(startYmd: string, endYmd: string): string {
  const start = utcDateFromYmd(startYmd);
  const end = utcDateFromYmd(endYmd);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return `${startYmd} – ${endYmd}`;
  }
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const startDay = start.getUTCDate();
  const endDay = end.getUTCDate();
  const startMonth = months[start.getUTCMonth()];
  const endMonth = months[end.getUTCMonth()];
  const startYear = start.getUTCFullYear();
  const endYear = end.getUTCFullYear();
  if (startYmd === endYmd) return `${startDay} ${startMonth} ${startYear}`;
  if (startYear === endYear && start.getUTCMonth() === end.getUTCMonth()) {
    return `${startDay}–${endDay} ${endMonth} ${endYear}`;
  }
  if (startYear === endYear) {
    return `${startDay} ${startMonth} – ${endDay} ${endMonth} ${endYear}`;
  }
  return `${startDay} ${startMonth} ${startYear} – ${endDay} ${endMonth} ${endYear}`;
}

export function selectedReportingDateRange(
  config: ReportingCalendarConfig,
  filter: ReportingDateFilter,
): { start: string; end: string } | null {
  const buckets = listTrendBuckets(config, filter);
  if (buckets.length === 0) return null;
  let start = buckets[0].start;
  let end = buckets[0].end;
  for (const bucket of buckets) {
    if (bucket.start < start) start = bucket.start;
    if (bucket.end > end) end = bucket.end;
  }
  return { start, end };
}

export function fyStartMonthLabel(month: number): string {
  return MONTH_NAMES[normalizeFyStartMonth(month) - 1] ?? "January";
}

export function weekStartsOnLabel(weekStartsOn: WeekStartsOn): string {
  return weekStartsOn === 0 ? "Sunday" : "Monday";
}

/**
 * Parse a date-like value to YYYY-MM-DD.
 * Date-only ISO strings are used as-is to avoid timezone shifts.
 */
export function ymdFromUnknown(value: unknown): string | null {
  if (value == null || value === "") return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return ymdFromParts(value.getFullYear(), value.getMonth() + 1, value.getDate());
  }
  if (value && typeof value === "object") {
    const asObj = value as Record<string, unknown>;
    const secondsRaw = asObj.seconds ?? asObj._seconds ?? asObj.sec ?? asObj.epochSeconds ?? asObj.unixSeconds;
    if (typeof secondsRaw === "number" && Number.isFinite(secondsRaw)) {
      const dt = new Date(secondsRaw * 1000);
      if (!Number.isNaN(dt.getTime())) return ymdFromParts(dt.getFullYear(), dt.getMonth() + 1, dt.getDate());
    }
    const millisRaw = asObj.milliseconds ?? asObj._milliseconds ?? asObj.ms ?? asObj.timestamp ?? asObj.epochMillis;
    if (typeof millisRaw === "number" && Number.isFinite(millisRaw)) {
      const dt = new Date(millisRaw);
      if (!Number.isNaN(dt.getTime())) return ymdFromParts(dt.getFullYear(), dt.getMonth() + 1, dt.getDate());
    }
    if (typeof (asObj as { toDate?: () => Date }).toDate === "function") {
      const dt = (asObj as { toDate: () => Date }).toDate();
      if (dt instanceof Date && !Number.isNaN(dt.getTime())) {
        return ymdFromParts(dt.getFullYear(), dt.getMonth() + 1, dt.getDate());
      }
    }
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const ms = value < 1_000_000_000_000 ? value * 1000 : value;
    const dt = new Date(ms);
    if (!Number.isNaN(dt.getTime())) return ymdFromParts(dt.getFullYear(), dt.getMonth() + 1, dt.getDate());
  }
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const isoDate = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/.exec(raw);
  if (isoDate) return `${isoDate[1]}-${isoDate[2]}-${isoDate[3]}`;
  if (/^\d{10,13}$/.test(raw)) {
    const n = Number.parseInt(raw, 10);
    const ms = raw.length <= 10 ? n * 1000 : n;
    const dt = new Date(ms);
    if (!Number.isNaN(dt.getTime())) return ymdFromParts(dt.getFullYear(), dt.getMonth() + 1, dt.getDate());
  }
  const isoLike = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[T\s].*)?$/.exec(raw);
  if (isoLike) {
    const month = Number.parseInt(isoLike[2], 10);
    const day = Number.parseInt(isoLike[3], 10);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return ymdFromParts(Number.parseInt(isoLike[1], 10), month, day);
    }
  }
  const ukLike = /^(\d{1,2})[-/](\d{1,2})[-/](\d{2}|\d{4})(?:[T\s].*)?$/.exec(raw);
  if (ukLike) {
    const day = Number.parseInt(ukLike[1], 10);
    const month = Number.parseInt(ukLike[2], 10);
    const yearRaw = Number.parseInt(ukLike[3], 10);
    const year = yearRaw < 100 ? 2000 + yearRaw : yearRaw;
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return ymdFromParts(year, month, day);
    }
  }
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return ymdFromParts(parsed.getFullYear(), parsed.getMonth() + 1, parsed.getDate());
  }
  return null;
}

export function currentReportingLocation(config: ReportingCalendarConfig, now = new Date()): ReportingDateLocation | null {
  const ymd = ymdFromParts(now.getFullYear(), now.getMonth() + 1, now.getDate());
  return locateDateInReportingCalendar(ymd, config);
}

export type ReportingCalendarState = {
  fyStartMonth: number;
  weekStartsOn: WeekStartsOn;
  p1CommencementMd: string;
  fyLabel: string;
  period1Start: string | null;
  current: ReportingDateLocation | null;
  periods: Array<{ period: number; weeks: number; start: string; end: string }>;
  weeks: Array<{ weekInYear: number; period: number; weekInPeriod: number; start: string; end: string }>;
  grain: ReportingTrendGrain;
};

export function serializeReportingCalendarState(
  config: ReportingCalendarConfig,
  filter: ReportingDateFilter,
  now = new Date(),
): ReportingCalendarState {
  const current = currentReportingLocation(config, now);
  const year = filter.years[0] ?? current?.fyStartYear ?? now.getFullYear();
  const periods = listPeriods(year, config);
  const weeks = listPeriodWeeks(year, config);
  return {
    fyStartMonth: config.fyStartMonth,
    weekStartsOn: config.weekStartsOn,
    p1CommencementMd: config.p1CommencementMd,
    fyLabel: formatFinancialYearLabel(year, config.fyStartMonth),
    period1Start: periods[0]?.start ?? null,
    current,
    periods: periods.map((period) => ({
      period: period.period,
      weeks: period.weeks,
      start: period.start,
      end: period.end,
    })),
    weeks: weeks.map((week) => ({
      weekInYear: week.weekInYear,
      period: week.period,
      weekInPeriod: week.weekInPeriod,
      start: week.start,
      end: week.end,
    })),
    grain: resolveTrendGrain(filter),
  };
}
